import type { TFunction } from 'i18next';
import { ApiError } from '../api/client';
import type { Person, Snapshot } from '../api/types';
import { planLimitInfo } from '../features/billing/plan-limit';

/** Turns `some_machine_code` into "Some machine code" when no translation exists. */
export function humanize(code: string): string {
  const s = code.replace(/[_.-]+/g, ' ').trim();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * Label for a backend enum value under `group` (e.g. `historyKind.created`). Unknown values
 * (newer backend) fall back to a readable form instead of a raw key.
 */
export function codeLabel(t: TFunction, group: string, code: string | null | undefined): string {
  if (!code) return '—';
  const key = `${group}.${code}`;
  const dyn = t as unknown as (k: string, o?: object) => string;
  const value = dyn(key, { defaultValue: '' });
  return value && value !== key ? value : humanize(code);
}

/** Where a snapshot came from; CI uploads name their CI when the job said which (FR-INV-14). */
export function snapshotSourceLabel(
  t: TFunction,
  s: Pick<Snapshot, 'source' | 'ciProvider'>,
): string {
  return codeLabel(
    t,
    'snapshotSource',
    s.source === 'ci' && s.ciProvider ? `ci_${s.ciProvider}` : s.source,
  );
}

/**
 * A message the API sends as a stable `code` with `params` and English text (parse reports,
 * upload errors, MCP setup steps): translated by code under `group`, else the English text,
 * else a readable form of the code. A translation whose placeholders the params do not fill
 * falls back to the English text too.
 */
export function codedMessage(
  t: TFunction,
  group: string,
  code: string,
  params: Record<string, string | number> = {},
  fallback?: string | null,
): string {
  const key = `${group}.${code}`;
  const dyn = t as unknown as (k: string, o?: object) => string;
  const value = dyn(key, { ...params, defaultValue: '' });
  if (value && value !== key && !value.includes('{{')) return value;
  return fallback || humanize(code);
}

/**
 * FR-BIL-01 plan-limit message with the numbers from the error (`402 PLAN_LIMIT_REACHED`,
 * `402 PLAN_FEATURE_UNAVAILABLE`), or null for any other error.
 */
export function planLimitMessage(t: TFunction, error: unknown): string | null {
  const info = planLimitInfo(error);
  if (!info) return null;
  const dyn = t as unknown as (k: string, o?: object) => string;
  const plan = dyn(`planTier.${info.tier}`);
  if (info.kind === 'limit') {
    return dyn(`planLimit.${info.resource}`, { plan, limit: info.limit, used: info.used });
  }
  const feature = dyn(`planLimit.feature.${info.feature}`, { defaultValue: '' });
  return dyn('planLimit.featureUnavailable', {
    plan,
    feature: feature || humanize(info.feature),
  });
}

/** Message for an error, translated by its stable API code. */
export function errorMessage(t: TFunction, error: unknown): string {
  const dyn = t as unknown as (k: string, o?: object) => string;
  const plan = planLimitMessage(t, error);
  if (plan) return plan;
  if (error instanceof ApiError) {
    const msg = dyn(`errors.${error.code}`, { defaultValue: '' });
    if (msg) return msg;
    return dyn('errors.UNKNOWN', { code: error.code });
  }
  if (error instanceof TypeError) return dyn('errors.NETWORK');
  return dyn('errors.UNKNOWN', { code: 'CLIENT' });
}

/**
 * Who a history or audit entry names: Smoke, the person (marked once they left the workspace),
 * a deleted account, or the actor type when the person is unknown (Smoke staff).
 */
export function personLabel(
  t: TFunction,
  people: readonly Person[],
  userId: string | null | undefined,
  actorType = 'user',
): string {
  if (actorType === 'system') return t('history.smoke');
  const p = userId ? people.find((x) => x.userId === userId) : undefined;
  if (!p) return codeLabel(t, 'actorType', actorType);
  if (p.deleted || !p.name) return t('people.deleted');
  return p.member ? p.name : t('people.left', { name: p.name });
}
